
import { auth } from '../firebase';
import { getUserPinState, putUserPinState, getUserSettings, putUserSettings } from '../sync/api-client';
// lib/db/queries.ts — All IndexedDB CRUD helpers

import { getDB } from './schema';
import type {
  Group, PlannedTransaction, AdHocTransaction, RecurringTransaction,
  MonthlySnapshot, AllocationHistory, SyncLog, PinState, Transaction
} from '../../types';
import { nanoid } from '../../lib/nanoid';


function emitDataChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('sorted-data-changed'));
  }
}


// ---- Groups ----

export async function getGroups(userId: string): Promise<Group[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('groups', 'by-userId', userId);
  return all.filter((g: Group) => !g.isDeleted);
}

export async function getGroup(groupId: string): Promise<Group | undefined> {
  const db = await getDB();
  const g = await db.get('groups', groupId);
  return g && !g.isDeleted ? g : undefined;
}

export async function putGroup(group: Group): Promise<void> {
  const db = await getDB();
  await db.put('groups', { ...group, updatedAt: new Date().toISOString() });
  emitDataChanged();
}

export async function deleteGroup(groupId: string): Promise<void> {
  const db = await getDB();
  const txn = db.transaction(['groups', 'transactions', 'allocationHistory'], 'readwrite');
  const now = new Date().toISOString();

  const group = await txn.objectStore('groups').get(groupId);
  if (group) {
    await txn.objectStore('groups').put({ ...group, isDeleted: true, deletedAt: now, updatedAt: now });
  }

  // Delete all transactions in group (soft delete)
  const txnsIndex = txn.objectStore('transactions').index('by-groupId');
  const txnsKeys = await txnsIndex.getAllKeys(groupId);
  const txns = await Promise.all(txnsKeys.map(k => txn.objectStore('transactions').get(k)));
  for (const t of txns) {
    if (t) {
      await txn.objectStore('transactions').put({ ...t, isDeleted: true, deletedAt: now, updatedAt: now });
    }
  }

  await txn.done;
  emitDataChanged();
}

export function createGroup(userId: string, data: Partial<Group>): Group {
  const now = new Date().toISOString();
  return {
    groupId: `grp_${nanoid()}`,
    userId,
    name: data.name ?? 'New Fund',
    description: data.description,
    color: data.color ?? '#7B6FFF',
    icon: data.icon ?? '💼',
    totalSaved: 0,
    totalTarget: 0,
    createdAt: now,
    updatedAt: now,
    schemaVersion: 1,
  };
}

// ---- Transactions ----

export async function getTransactions(groupId: string): Promise<Transaction[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('transactions', 'by-groupId', groupId) as Transaction[];
  return all.filter(t => !t.isDeleted);
}

export async function getTransaction(transactionId: string): Promise<Transaction | undefined> {
  const db = await getDB();
  const t = await db.get('transactions', transactionId) as Transaction | undefined;
  return t && !t.isDeleted ? t : undefined;
}

export async function getAllTransactionsByUser(userId: string): Promise<Transaction[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('transactions', 'by-userId', userId) as Transaction[];
  return all.filter(t => !t.isDeleted);
}

export async function putTransaction(txn: Transaction): Promise<void> {
  const db = await getDB();
  await db.put('transactions', { ...txn, updatedAt: new Date().toISOString() });
  emitDataChanged();
}

export async function deleteTransaction(transactionId: string): Promise<void> {
  const db = await getDB();
  const t = await db.get('transactions', transactionId);
  if (t) {
    const now = new Date().toISOString();
    await db.put('transactions', { ...t, isDeleted: true, deletedAt: now, updatedAt: now });
  }

  emitDataChanged();
}

export function createPlannedTransaction(
  userId: string,
  groupId: string,
  data: Partial<PlannedTransaction>
): PlannedTransaction {
  const now = new Date().toISOString();
  return {
    transactionId: `txn_${nanoid()}`,
    groupId,
    userId,
    type: 'planned',
    name: data.name ?? '',
    amount: data.amount ?? 0,
    description: data.description,
    dueDate: data.dueDate ?? now,
    startDate: data.startDate ?? now,
    frequency: data.frequency,
    currentAllocation: data.currentAllocation ?? 0,
    status: 'saving',
    paidAmount: 0,
    savedHistory: {},
    skippedMonths: data.skippedMonths ?? [],
    category: data.category,
    paymentMethod: data.paymentMethod,
    reference: data.reference,
    createdAt: now,
    updatedAt: now,
    schemaVersion: 1,
  };
}

export function createAdHocTransaction(
  userId: string,
  groupId: string,
  data: Partial<AdHocTransaction>
): AdHocTransaction {
  const now = new Date().toISOString();
  return {
    transactionId: `txn_${nanoid()}`,
    groupId,
    userId,
    type: 'adhoc',
    name: data.name ?? '',
    amount: data.amount ?? 0,
    description: data.description,
    date: data.date ?? now,
    poolsFromGroup: data.poolsFromGroup ?? false,
    status: 'pending',
    paymentMethod: data.paymentMethod,
    reference: data.reference,
    createdAt: now,
    updatedAt: now,
    schemaVersion: 1,
  };
}

export function createRecurringTransaction(
  userId: string,
  groupId: string,
  data: Partial<RecurringTransaction>
): RecurringTransaction {
  const now = new Date().toISOString();
  return {
    transactionId: `txn_${nanoid()}`,
    groupId,
    userId,
    type: 'recurring',
    name: data.name ?? '',
    amount: data.amount ?? 0,
    description: data.description,
    startDate: data.startDate ?? now,
    endDate: data.endDate,
    frequency: 'monthly',
    status: data.status ?? 'active',
    paymentHistory: data.paymentHistory ?? {},
    skippedMonths: data.skippedMonths ?? [],
    category: data.category,
    paymentMethod: data.paymentMethod,
    reference: data.reference,
    createdAt: now,
    updatedAt: now,
    schemaVersion: 1,
  };
}

// ---- Allocation History ----

export async function getAllocationHistory(transactionId: string): Promise<AllocationHistory[]> {
  const db = await getDB();
  return db.getAllFromIndex('allocationHistory', 'by-transactionId', transactionId);
}

export async function getAllocationHistoryByUser(userId: string): Promise<AllocationHistory[]> {
  const db = await getDB();
  return db.getAllFromIndex('allocationHistory', 'by-userId', userId);
}

export async function putAllocationHistory(entry: AllocationHistory): Promise<void> {
  const db = await getDB();
  await db.put('allocationHistory', entry);
}

// ---- Monthly Snapshots ----

export async function getMonthlySnapshot(userId: string, yearMonth: string): Promise<MonthlySnapshot | undefined> {
  const db = await getDB();
  return db.get('monthlySnapshots', [userId, yearMonth]);
}

export async function putMonthlySnapshot(snapshot: MonthlySnapshot): Promise<void> {
  const db = await getDB();
  await db.put('monthlySnapshots', { ...snapshot, calculatedAt: new Date().toISOString() });
}

// ---- Sync Log ----

export async function getLatestSyncLog(userId: string): Promise<SyncLog | undefined> {
  const db = await getDB();
  const all = await db.getAllFromIndex('syncLog', 'by-userId', userId);
  return all.sort((a, b) => b.lastSyncedAt.localeCompare(a.lastSyncedAt))[0];
}

export async function getRecentSyncLogs(userId: string, limit = 5): Promise<SyncLog[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('syncLog', 'by-userId', userId);
  return all.sort((a, b) => b.lastSyncedAt.localeCompare(a.lastSyncedAt)).slice(0, limit);
}

export async function putSyncLog(entry: SyncLog): Promise<void> {
  const db = await getDB();
  await db.put('syncLog', entry);
}

// ---- PIN State ----

export async function getPinState(userId: string): Promise<(PinState & { userId: string }) | undefined> {
  const db = await getDB();
  const localPinState = await db.get('pinState', userId);

  // Sync from backend API if it's a cloud account and missing locally
  if (!localPinState) {
    try {
      if (auth.currentUser) {
        const remotePinState = await getUserPinState();
        if (remotePinState && Object.keys(remotePinState).length > 0) {
          await db.put('pinState', { ...remotePinState, userId });
          return { ...remotePinState, userId };
        }
      }
    } catch (e) {
      console.warn('Failed to load userPinState from API', e);
    }
  }

  return localPinState;
}

export async function putPinState(userId: string, state: PinState): Promise<void> {
  const db = await getDB();
  await db.put('pinState', { ...state, userId });

  // Sync to backend API if it's a cloud account
  try {
    if (auth.currentUser) {
      await putUserPinState(state);
    }
  } catch (e) {
    console.warn('Failed to save userPinState to API', e);
  }
}

// ---- Settings ----

export async function getSettings(userId: string) {
  const db = await getDB();
  const localSettings = await db.get('settings', userId);

  let mergedSettings = {
    userId,
    onboardingComplete: false,
    autoSync: false,
    notifyUpcomingBills: true,
    notifyAllocationChanges: true,
    notifySyncStatus: false,
    currency: 'RM',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    driveConnected: false,
    driveEmail: null,
    driveName: null,
    ...(localSettings || {})
  };

  if (localSettings) {
    // If local settings exist, return them immediately and fetch from API in background to update IndexedDB
    if (auth.currentUser) {
      getUserSettings().then(async (remoteSettings) => {
        if (remoteSettings && Object.keys(remoteSettings).length > 0) {
          const latestDb = await getDB();
          const currentLocal = await latestDb.get('settings', userId);
          const updated = { ...currentLocal, ...remoteSettings, userId };
          await latestDb.put('settings', updated as Parameters<typeof putSettings>[0]);
        }
      }).catch(e => {
        console.warn('Failed to load userSettings from API in background', e);
      });
    }
    return mergedSettings as NonNullable<typeof localSettings>;
  }

  // Sync from backend API if it's a cloud account and local settings don't exist yet
  try {
    if (auth.currentUser) {
      const remoteSettings = await getUserSettings();
      if (remoteSettings && Object.keys(remoteSettings).length > 0) {
        mergedSettings = { ...mergedSettings, ...remoteSettings, userId };
        await db.put('settings', mergedSettings as Parameters<typeof putSettings>[0]);
      }
    }
  } catch (e) {
    console.warn('Failed to load userSettings from API', e);
  }

  return mergedSettings as NonNullable<typeof localSettings>;
}

export async function putSettings(settings: Awaited<ReturnType<typeof getSettings>>): Promise<void> {
  const db = await getDB();
  await db.put('settings', settings);
  emitDataChanged();

  // Sync to backend API if it's a cloud account
  try {
    if (auth.currentUser) {
      // Before uploading, strip the pending flag so we don't upload it (though backend would just merge it)
      const { pendingSettingsSync: _, ...payload } = settings;
      await putUserSettings(payload);

      // If we previously had pending changes, clear them since we just succeeded
      if (settings.pendingSettingsSync) {
        await db.put('settings', { ...settings, pendingSettingsSync: false });
      }
    }
  } catch (e) {
    console.warn('Failed to save userSettings to API, queueing for offline sync', e);
    // Mark as pending offline sync
    await db.put('settings', { ...settings, pendingSettingsSync: true });
  }
}

// ---- Offline Sync Queue ----

export async function syncPendingSettings(userId: string) {
  try {
    if (!auth.currentUser) return;

    const db = await getDB();
    const settings = await db.get('settings', userId);

    if (settings?.pendingSettingsSync) {
      const { pendingSettingsSync: _, ...payload } = settings;
      await putUserSettings(payload);

      // Success, remove flag
      await db.put('settings', { ...settings, pendingSettingsSync: false });
      console.info('[Offline] Successfully pushed pending user settings to API.');
    }
  } catch (err) {
    console.warn('[Offline] Pending settings push failed, will retry later:', err);
  }
}

// ---- Export all data for sync blob ----

async function getMonthlySnapshotsByUser(userId: string): Promise<MonthlySnapshot[]> {
  const db = await getDB();
  return db.getAllFromIndex('monthlySnapshots', 'by-userId', userId);
}

export async function exportAllData(userId: string) {
  const [groups, transactions, snapshots, history, syncLogs] = await Promise.all([
    getGroups(userId),
    getAllTransactionsByUser(userId),
    getMonthlySnapshotsByUser(userId),
    getAllocationHistoryByUser(userId),
    getRecentSyncLogs(userId, 20),
  ]);

  return { groups, transactions, snapshots, history, syncLogs, exportedAt: new Date().toISOString() };
}

// ---- Clear all local data for a user (used on sign-out & clear data) ----

export async function clearAllUserData(userId: string): Promise<void> {
  const db = await getDB();

  // Delete all groups belonging to this user
  const groups = await db.getAllFromIndex('groups', 'by-userId', userId);
  for (const g of groups) {
    await db.delete('groups', g.groupId);
  }

  // Delete all transactions belonging to this user
  const txns = await db.getAllFromIndex('transactions', 'by-userId', userId);
  for (const t of txns) {
    await db.delete('transactions', t.transactionId);
  }

  // Delete monthly snapshots
  const snapshots = await db.getAllFromIndex('monthlySnapshots', 'by-userId', userId);
  for (const s of snapshots) {
    await db.delete('monthlySnapshots', [s.userId, s.yearMonth]);
  }

  // Delete allocation history
  const history = await db.getAllFromIndex('allocationHistory', 'by-userId', userId);
  for (const h of history) {
    await db.delete('allocationHistory', h.historyId);
  }

  // Delete sync logs
  const syncLogs = await db.getAllFromIndex('syncLog', 'by-userId', userId);
  for (const l of syncLogs) {
    await db.delete('syncLog', l.syncId);
  }

  // Delete PIN state
  try { await db.delete('pinState', userId); } catch { /* may not exist */ }

  // Delete settings
  try { await db.delete('settings', userId); } catch { /* may not exist */ }

  console.info('[DB] Cleared all local data for user:', userId);
}
